"use client";

import React, { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import {
  DynamicFieldOptions,
  DynamicOptionValue,
  FormField,
  OptionSource,
} from "@/store/types/aichat";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface ServerDrivenFormProps {
  fields: FormField[];
  dynamicOptions?: DynamicFieldOptions;
  onSubmit: (data: Record<string, string>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const ServerDrivenForm: React.FC<ServerDrivenFormProps> = ({
  fields,
  dynamicOptions = {},
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [visibleFields, setVisibleFields] = useState<string[]>([]);

  const matchesCondition = (
    condition:
      | FormField["visible_if"]
      | FormField["required_if"]
      | undefined,
    data: Record<string, string> = formData,
  ) => {
    if (!condition) return true;

    const dependentValue = data[condition.field] || "";
    if (!dependentValue) return false;

    if (condition.equals !== undefined) {
      return dependentValue === condition.equals;
    }

    if (condition.not_equals !== undefined) {
      return dependentValue !== condition.not_equals;
    }

    return true;
  };

  const isFieldVisible = (
    field: FormField,
    data: Record<string, string> = formData,
  ) => matchesCondition(field.visible_if, data);

  const isFieldRequired = (
    field: FormField,
    data: Record<string, string> = formData,
  ) => {
    if (field.required_if) {
      return matchesCondition(field.required_if, data);
    }

    return Boolean(field.required);
  };

  const handleChange = (name: string, value: string) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      // Clear dependent field value when parent changes and selection is no longer valid.
      fields.forEach((field) => {
        if (field.depends_on !== name) return;

        const dependentSource = resolveOptionSource(field, value);
        const dependentOptions = normalizeOptionSource(
          field.options || [],
          dependentSource,
          field,
        ).allOptions;
        const currentDependentValue = next[field.name];

        if (
          currentDependentValue &&
          !dependentOptions.includes(currentDependentValue)
        ) {
          next[field.name] = "";
        }

        if (!isFieldVisible(field, next)) {
          next[field.name] = "";
        }
      });

      fields.forEach((field) => {
        if (!isFieldVisible(field, next) && next[field.name]) {
          next[field.name] = "";
        }
      });

      return next;
    });

    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const formatOption = (str: string) => {
    if (!str) return str;

    const normalized = str.replace(/^europe\s*-\s*/i, "");

    return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const currentYear = new Date().getFullYear();

    fields.forEach((field) => {
      if (!isFieldVisible(field)) return;

      const val = formData[field.name];

      if (isFieldRequired(field) && (!val || !val.trim())) {
        newErrors[field.name] = `${field.label} is required`;
        return;
      }

      if (val && val.trim()) {
        const nameLower = field.name.toLowerCase();
        const labelLower = field.label.toLowerCase();
        const numVal = parseFloat(val);

        if (
          nameLower.includes("percentage") ||
          labelLower.includes("percentage") ||
          nameLower.includes("marks") ||
          labelLower.includes("marks")
        ) {
          if (!isNaN(numVal) && (numVal < 0 || numVal > 100)) {
            newErrors[field.name] = `${field.label} must be between 0 and 100`;
          }
        }

        if (nameLower.includes("year") || labelLower.includes("year")) {
          if (!isNaN(numVal) && numVal > currentYear) {
            newErrors[field.name] =
              `${field.label} must be ${currentYear} or earlier`;
          }
        }

        if (
          nameLower.includes("score") ||
          labelLower.includes("score") ||
          nameLower.includes("english") ||
          labelLower.includes("english")
        ) {
          if (!isNaN(numVal)) {
            const formContext = JSON.stringify(formData).toLowerCase();
            if (
              formContext.includes("ielts") ||
              nameLower.includes("ielts") ||
              labelLower.includes("ielts")
            ) {
              if (numVal < 0 || numVal > 9)
                newErrors[field.name] = "IELTS score must be between 0 and 9";
            } else if (
              formContext.includes("toefl") ||
              nameLower.includes("toefl") ||
              labelLower.includes("toefl")
            ) {
              if (numVal < 0 || numVal > 120)
                newErrors[field.name] = "TOEFL score must be between 0 and 120";
            } else if (
              formContext.includes("pte") ||
              nameLower.includes("pte") ||
              labelLower.includes("pte")
            ) {
              if (numVal < 10 || numVal > 90)
                newErrors[field.name] = "PTE score must be between 10 and 90";
            } else if (
              formContext.includes("duolingo") ||
              nameLower.includes("duolingo") ||
              labelLower.includes("duolingo")
            ) {
              if (numVal < 10 || numVal > 160)
                newErrors[field.name] =
                  "Duolingo score must be valid (max 160)";
            }
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const visibleFormData = Object.fromEntries(
      fields
        .filter((field) => isFieldVisible(field))
        .map((field) => [field.name, formData[field.name] || ""]),
    );

    onSubmit(visibleFormData);
  };

  const getFilteredOptions = (fieldName: string, options: string[]) => {
    const searchValue = (searchTerms[fieldName] || "").trim().toLowerCase();
    if (!searchValue) return options;

    return options.filter((option) =>
      formatOption(option).toLowerCase().includes(searchValue),
    );
  };

  const isStringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((item) => typeof item === "string");

  const isOptionSource = (value: unknown): value is OptionSource => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return false;
    }

    const source = value as OptionSource;
    return (
      source.options !== undefined ||
      source.all_options !== undefined ||
      source.has_more !== undefined ||
      source.total_options !== undefined ||
      source.max_visible !== undefined
    );
  };

  const normalizeOptionSource = (
    fallbackOptions: string[],
    source?: DynamicOptionValue,
    field?: FormField,
  ) => {
    const fieldOptions = isStringArray(field?.options) ? field.options : [];
    const fieldAllOptions = isStringArray(field?.all_options)
      ? field.all_options
      : [];
    const defaultVisible = fieldOptions.length ? fieldOptions : fallbackOptions;
    const defaultAll = fieldAllOptions.length ? fieldAllOptions : defaultVisible;

    if (!source) {
      const hasMore =
        Boolean(field?.has_more) || defaultAll.length > defaultVisible.length;
      const total =
        typeof field?.total_options === "number"
          ? field.total_options
          : defaultAll.length;

      return {
        visibleOptions: defaultVisible,
        allOptions: defaultAll,
        hasMore,
        totalOptions: total,
      };
    }

    if (isStringArray(source)) {
      return {
        visibleOptions: source,
        allOptions: source,
        hasMore: false,
        totalOptions: source.length,
      };
    }

    const visibleOptions = isStringArray(source.options)
      ? source.options
      : defaultVisible;
    const allOptions = isStringArray(source.all_options)
      ? source.all_options
      : visibleOptions;
    const hasMore = Boolean(source.has_more) || allOptions.length > visibleOptions.length;
    const totalOptions =
      typeof source.total_options === "number"
        ? source.total_options
        : allOptions.length;

    return { visibleOptions, allOptions, hasMore, totalOptions };
  };

  const resolveOptionSource = (
    field: FormField,
    parentValue: string,
  ): DynamicOptionValue | undefined => {
    if (!field.depends_on) return undefined;

    const fieldConfig = dynamicOptions[field.name];
    if (!fieldConfig || typeof fieldConfig !== "object") return undefined;

    const directMap = fieldConfig as Record<string, DynamicOptionValue>;
    const directMatch = directMap[parentValue];
    if (directMatch && (isStringArray(directMatch) || isOptionSource(directMatch))) {
      return directMatch;
    }

    const nestedMap = directMap[field.name];
    if (
      nestedMap &&
      typeof nestedMap === "object" &&
      !Array.isArray(nestedMap)
    ) {
      const nestedMatch = (nestedMap as Record<string, DynamicOptionValue>)[
        parentValue
      ];
      if (
        nestedMatch &&
        (isStringArray(nestedMatch) || isOptionSource(nestedMatch))
      ) {
        return nestedMatch;
      }
    }

    return undefined;
  };

  useEffect(() => {
    setSearchTerms({});
  }, [fields, dynamicOptions]);

  useEffect(() => {
    const nextFormData = fields.reduce<Record<string, string>>((acc, field) => {
      const rawValue = field.value;
      acc[field.name] =
        rawValue === undefined || rawValue === null || rawValue === "null"
          ? ""
          : String(rawValue);
      return acc;
    }, {});

    setFormData(nextFormData);
    setErrors({});
  }, [fields]);

  useEffect(() => {
    setVisibleFields([]);

    const timers = fields.map((field, index) =>
      window.setTimeout(() => {
        setVisibleFields((prev) =>
          prev.includes(field.name) ? prev : [...prev, field.name],
        );
      }, 180 * index),
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [fields]);

  return (
    <div className="space-y-4 ff_poppins">
      {fields.map((field, index) => {
        if (!visibleFields.includes(field.name) || !isFieldVisible(field)) {
          return null;
        }

        const parentValue = field.depends_on ? formData[field.depends_on] : "";
        const dependentSource = resolveOptionSource(field, parentValue || "");
        const optionConfig = normalizeOptionSource(
          field.options || [],
          dependentSource,
          field,
        );
        const filteredOptions = getFilteredOptions(
          field.name,
          optionConfig.allOptions,
        );
        const isDependentDisabled = !!field.depends_on && !parentValue;

        return (
          <div
            key={field.name}
            className="animate-fadeIn relative space-y-2"
            style={{ animationDelay: `${index * 80}ms` }}
          >
            <label className="block text-sm font-medium text-gray-700">
              {field.label}
              {isFieldRequired(field) && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>

            {field.type === "select" ? (
              <Select
                value={formData[field.name] || ""}
                onValueChange={(val) => {
                  handleChange(field.name, val);
                  setSearchTerms((prev) => ({ ...prev, [field.name]: "" }));
                }}
                disabled={isDependentDisabled}
              >
                <SelectTrigger className="w-full ff_poppins px-3 py-2 border rounded-lg bg-[#7C3AED] border-[#7C3AED] text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9D7AE8]/40 disabled:opacity-50">
                  <SelectValue
                    placeholder={
                      isDependentDisabled
                        ? `Select ${fields.find((f) => f.name === field.depends_on)?.label || "previous field"} first`
                        : field.placeholder || `Select ${field.label}`
                    }
                  >
                    {formData[field.name]
                      ? formatOption(formData[field.name])
                      : ""}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-[#7C3AED] border-[#7C3AED] ff_poppins max-h-60 w-full shadow-2xl">
                  <div className="sticky top-0 z-10 px-2 pb-2 pt-1 bg-[#7C3AED]">
                    <input
                      type="text"
                      value={searchTerms[field.name] || ""}
                      onChange={(e) =>
                        setSearchTerms((prev) => ({
                          ...prev,
                          [field.name]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder={`Search ${field.label}`}
                      className="text-white w-full px-3 py-2 border border-white/20 rounded-lg bg-[#7C3AED] text-sm focus:outline-none focus:ring-2 focus:ring-white/30 placeholder:text-white/60"
                    />
                  </div>
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => (
                      <SelectItem
                        key={option}
                        value={option}
                        className="text-white cursor-pointer hover:bg-[#9D7AE8] duration-300 ease-out hover:outline-none focus:outline-none focus:bg-[#9D7AE8] w-full"
                      >
                        <span className="truncate">{formatOption(option)}</span>
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-white/80">
                      No results found
                    </div>
                  )}
                </SelectContent>
              </Select>
            ) : (
              <input
                type={field.type === "number" ? "number" : "text"}
                value={formData[field.name] || ""}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder || `Enter ${field.label}`}
                className="text-white w-full px-3 py-2 border border-[#7C3AED] rounded-lg bg-[#7C3AED] text-sm focus:outline-none focus:ring-2 focus:ring-[#9D7AE8]/40 placeholder:text-white/60"
              />
            )}

            {errors[field.name] && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                <span className="break-words">{errors[field.name]}</span>
              </p>
            )}
          </div>
        );
      })}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex-1 py-2.5 rounded-xl bg-[linear-gradient(135deg,#9D7AE8_0%,#6B3FC8_100%)] hover:opacity-95 shadow-[0px_4px_12px_rgba(107,63,200,0.15)] font-semibold text-white transition-all duration-200 disabled:opacity-70 text-sm ff_poppins flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            "Submit"
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-[#7C3AED] hover:border-[#DDD6FE] transition-colors duration-200 text-sm font-medium ff_poppins"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ServerDrivenForm;
